import React from 'react'

import Button from 'components/Form/Button'
import SurveyQuestion from 'components/SurveyQuestion'
import SurveySkeleton from 'components/SurveySkeleton'

import useController from './controller'

import {
  CompletionMark,
  IntroBadge,
  IntroDescription,
  IntroMeta,
  IntroMetaItem,
  IntroPanel,
  IntroTitle,
  Navigation,
  PageRoot,
  ProgressBar,
  ProgressFill,
  ProgressHeader,
  ProgressLabel,
  ProgressSteps,
  QuestionSection,
  StateDescription,
  StatePanel,
  StateTitle,
  StepChip,
  SurveyDescription,
  SurveyHeader,
  SurveyMeta,
  SurveyTitle,
  SurveyWorkspace,
} from './styled'

export default function Survey() {
  const {
    answers,
    completion,
    currentError,
    currentIndex,
    currentQuestion,
    estimatedMinutes,
    isLastQuestion,
    loadError,
    loading,
    progressPercentage,
    questionCount,
    questionFocusRef,
    started,
    submitting,
    survey,
    t,
    handleAnswer,
    handleBack,
    handleBackToCourse,
    handleContinue,
    handleStart,
    handleSubmit,
  } = useController()

  return (
    <PageRoot>
      {loading ? (
        <SurveySkeleton />
      ) : loadError ? (
        <StatePanel>
          <CompletionMark>!</CompletionMark>
          <StateTitle>{t('survey_unavailable')}</StateTitle>
          <StateDescription>{loadError}</StateDescription>
          <Button color="blue" nospace width="fit-content" onClick={handleBackToCourse}>
            {t('survey_back_to_course')}
          </Button>
        </StatePanel>
      ) : completion ? (
        <StatePanel>
          <CompletionMark completed>✓</CompletionMark>
          <StateTitle>{t('survey_thank_you')}</StateTitle>
          <StateDescription>
            {completion?.completionMessage || survey?.completionMessage || t('survey_completed_description')}
          </StateDescription>
          {!completion?.submittedAt ? null : (
            <SurveyMeta>
              {t('survey_submitted')}
            </SurveyMeta>
          )}
          <Button color="blue" nospace width="fit-content" onClick={handleBackToCourse}>
            {t('survey_continue_course')}
          </Button>
        </StatePanel>
      ) : !started ? (
        <IntroPanel>
          <IntroBadge>{survey?.course?.title || t('survey_course_feedback')}</IntroBadge>
          <IntroTitle>{survey?.title}</IntroTitle>
          <IntroDescription>
            {survey?.description || t('survey_intro_description')}
          </IntroDescription>
          <IntroMeta>
            <IntroMetaItem>{questionCount} {t('survey_questions')}</IntroMetaItem>
            <IntroMetaItem>~{estimatedMinutes} {t('survey_minutes')}</IntroMetaItem>
            <IntroMetaItem>{t('survey_account_linked')}</IntroMetaItem>
          </IntroMeta>
          <Button color="blue" nospace width="190px" onClick={handleStart}>
            {t('survey_start')}
          </Button>
        </IntroPanel>
      ) : (
        <SurveyWorkspace>
          <ProgressHeader>
            <ProgressSteps aria-label={t('survey_progress')}>
              {(survey?.questions || []).map((question, index) => (
                <StepChip
                  active={index === currentIndex}
                  completed={index < currentIndex}
                  key={question?.id}
                >
                  {index < currentIndex ? '✓' : String(index + 1).padStart(2, '0')}
                </StepChip>
              ))}
            </ProgressSteps>
            <ProgressLabel>
              {currentIndex + 1} / {questionCount}
            </ProgressLabel>
          </ProgressHeader>
          <ProgressBar
            aria-label={t('survey_progress')}
            aria-valuemax="100"
            aria-valuemin="0"
            aria-valuenow={Math.round(progressPercentage)}
            role="progressbar"
          >
            <ProgressFill progress={progressPercentage} />
          </ProgressBar>

          <SurveyHeader>
            <SurveyMeta>{survey?.course?.title}</SurveyMeta>
            <SurveyTitle>{survey?.title}</SurveyTitle>
            {!survey?.description ? null : (
              <SurveyDescription>{survey.description}</SurveyDescription>
            )}
          </SurveyHeader>

          <QuestionSection key={currentQuestion?.id}>
            <SurveyQuestion
              disabled={submitting}
              error={currentError}
              focusRef={questionFocusRef}
              optionalLabel={t('survey_optional')}
              placeholder={t('survey_text_placeholder')}
              question={currentQuestion}
              requiredLabel={t('survey_required')}
              value={answers?.[currentQuestion?.id]}
              onChange={handleAnswer}
            />
          </QuestionSection>

          <Navigation>
            <Button
              outline
              color="white"
              disabled={submitting}
              nospace
              width="fit-content"
              onClick={handleBack}
            >
              {t('survey_back')}
            </Button>
            <Button
              color="blue"
              disabled={submitting}
              loading={submitting}
              nospace
              width="fit-content"
              onClick={isLastQuestion ? handleSubmit : handleContinue}
            >
              {isLastQuestion ? t('survey_submit') : t('survey_continue')}
            </Button>
          </Navigation>
        </SurveyWorkspace>
      )}
    </PageRoot>
  )
}
